// VqeEditContext — shared edit-context shape passed down from
// the Visual view or the compact popover (`useVqeEditWiring`) to
// individual render components that surface edit affordances.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove SELECT
// column.
//
// The context bundles every dependency `commitEdit` needs PLUS the resolver
// service and the tab/document/statement identity that drives suggestions and
// edit dispatch. Render components treat this prop as **optional** — when
// undefined, the existing disabled scaffolding renders unchanged. This keeps
// VQE-RO unit tests passing without per-test stubbing.

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';
import type { QueryStructure } from '../../../sql-resolver/types';
import type { Scope } from '../../../sql-resolver/types/scope';
import type { CommitEditCtx, MonacoEditorLike, MonacoTextModelLike } from './commitEdit';

export type VqeEditContext = {
  readonly service: SqlResolverService;
  readonly documentId: string;
  readonly statementId: string;
  /** The Query Editor tab the buffer belongs to — its last results feed the filter chip's sample. */
  readonly tabId?: string;
  readonly model: MonacoTextModelLike;
  readonly editor?: MonacoEditorLike;
  /**
   * Authoritative CURRENT buffer text (Monaco value / workspace-store tab
   * query). Forwarded to `commitEdit` so it can flush a stale resolver to the
   * live text before resolving and guard against the buffer moving mid-commit
   * (P12 §e stale-buffer race). Optional — read-only affordance callers never
   * commit, so they can omit it.
   */
  readonly getLiveSource?: () => string;
  /**
   * The Resolver scope rendered controls should consult for suggestions.
   * Pre-resolved per-statement (the workspace tab knows the statement; the
   * inline toolbar resolves at the cursor) so render components don't have to
   * re-resolve on every render.
   */
  readonly scope: Scope | null | undefined;
  /**
   * Per-branch StatementInfo override for VQE set-op branch editing (VQE-3).
   * Set per-call by branch-level callers (`deriveBranchEditContext`, Slice 3),
   * NOT by the top-level tab context builder. When present, `toCommitCtx`
   * forwards it so `resolveEdit` routes through `applyStructuralEditForStatement`
   * instead of the id-based `applyStructuralEdit` path.
   */
  readonly statementInfo?: StatementInfo;
};

/**
 * Extract the `QueryStructure` for an edit context, honouring a per-call
 * `statementInfo` override.
 *
 * Branch (set-op) and drilled-subquery contexts carry a SYNTHETIC `statementInfo`
 * whose id (`<parent>.sub.START-END`) is NOT in `documentStructure.statements`.
 * Routing such an id through the id-based `extractQueryStructure` throws
 * (`requireStatement`), which the affordances catch and degrade to read-only.
 * When `statementInfo` is present we use the range-addressable
 * `extractQueryStructureForStatement` instead; otherwise we fall through to the
 * id-based path (top-level statements — behaviour unchanged). This is THE
 * canonical structure accessor for every VQE edit affordance.
 */
export function extractStructureForContext(ctx: VqeEditContext): QueryStructure {
  return ctx.statementInfo
    ? ctx.service.extractQueryStructureForStatement(ctx.documentId, ctx.statementInfo)
    : ctx.service.extractQueryStructure(ctx.documentId, ctx.statementId);
}

/** The context statement's text span in the parsed buffer (a synthetic body's own range); null when unknown. */
export function statementSpanOf(
  ctx: VqeEditContext,
): { readonly source: string; readonly start: number; readonly end: number; readonly options: ParseOptions } | null {
  const buffer = ctx.service.getParsedBuffer(ctx.documentId);
  const range =
    ctx.statementInfo?.range ?? buffer?.documentStructure.statements.find((s) => s.id === ctx.statementId)?.range;
  if (!buffer || !range) return null;
  const { source, options } = buffer.parsedDocument;
  return { source, start: range.startOffset, end: range.endOffset, options };
}

/**
 * Build the `commitEdit` ctx slice from a VqeEditContext.
 * Forwards `statementInfo` so branch-level contexts (Slice 3
 * `deriveBranchEditContext`) reach the per-branch apply path in `resolveEdit`.
 */
export function toCommitCtx(ctx: VqeEditContext): CommitEditCtx {
  return {
    service: ctx.service,
    documentId: ctx.documentId,
    statementId: ctx.statementId,
    statementInfo: ctx.statementInfo,
    model: ctx.model,
    editor: ctx.editor,
    getLiveSource: ctx.getLiveSource,
  };
}
